import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Typography from '@material-ui/core/Typography';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { LOCATIONS_EMMY_AWARDS_PAGE } from '@/constants/locations';
import { ROUTE_EMMY_AWARDS, ROUTE_PARTNER_EMMY_AWARDS } from '@/constants/routes';
import { EMMY_AWARDS_PAGE } from '@/constants/swimlanes';

// UTILITY
import { analyticsPageLoaded } from '@/utility/analytics';
import { getS3ImageUrl } from '@/utility/content';
import { getLink } from '@/utility/routes';

// HOC
import Layout from '@/hoc/Layout';

// COMPONENTS
import Swimlanes from '@/components/Swimlanes';
import CreateFreeProfile from '@/containers/Home/help/CreateFreeProfile';

// STORE
import { AppDetailsActions, EmmyAwardsActions, SwimlanesActions } from '@/store/actions';

// HELP
import Hero from './help/Hero';

// STYLES
import { Container } from './styles';

const defaultOGImg = getS3ImageUrl('/emmy-og2.png', '/cp-uploads');

class EmmyAwards extends PureComponent {
  componentDidMount() {
    const { getEmmyAwardsDetails } = this.props;

    getEmmyAwardsDetails();
  }

  componentWillUnmount() {
    const { swimlanesClear } = this.props;

    swimlanesClear();
  }

  handleUpdateAppStatus = (data, status) => {
    const { appListUpdateAppStatus } = this.props;

    appListUpdateAppStatus(data.id, status);
  };

  render() {
    const {
      t,
      isAuth,
      section1,
      section2,
      section1Loadings,
      section2Loadings,
      partner,
      partnerLoading,
    } = this.props;
    const isPartner = !!partner.slug;
    const hasSection2 = !!Object.keys(section2).length;

    const rightContent = (
      <>
        <img className="bg" src={getS3ImageUrl('/emmy-bg2.png')} alt="emmy-bg" />
        <img className="image" src={getS3ImageUrl('/emmy-image.png')} alt="emmy-img" />
      </>
    );

    const seoData = {
      canonicalUrl: `${siteUrl}${ROUTE_EMMY_AWARDS}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_EMMY_AWARDS, { partner: partner.slug })}`
        : `${siteUrl}${ROUTE_EMMY_AWARDS}`,
      title: 'Emmy Awards',
      description: `MyBundle - ${t('emmyAwards.hero.description')}`,
      image: defaultOGImg,
    };

    return (
      <Layout>
        {!partnerLoading && (
          <Helmet
            onChangeClientState={() => analyticsPageLoaded(ROUTE_EMMY_AWARDS)}
            titleTemplate="%s | MyBundle"
          >
            <title>{seoData.title}</title>
            <meta property="og:title" content={seoData.title} />
            <meta property="og:site_name" content="MyBundle" />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta property="og:description" content={seoData.description} />
            <meta name="description" content={seoData.description} />

            <meta property="og:type" content="website" />
            <meta property="fb:app_id" content="608514030003288" />
            <meta property="og:image" content={seoData.image} />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta name="twitter:title" content={seoData.title} />
            <meta name="twitter:description" content={seoData.description} />
            <meta name="twitter:image" content={seoData.image} />
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
          </Helmet>
        )}
        <Container className="emmy-awards">
          <Hero
            className="emmy-awards__hero"
            title={t('emmyAwards.hero.title')}
            subtitle={t('emmyAwards.hero.subtitle')}
            description={t('emmyAwards.hero.description')}
            rightContent={rightContent}
          />
          <div className="section emmy-awards__content">
            <div className="page-content">
              <Typography className="m-b-15" variant="h3" align="center">
                {t('emmyAwards.title')}
              </Typography>
              <Typography className="p3 emmy-awards__description" align="center">
                {t('emmyAwards.description')}
              </Typography>
              <Swimlanes
                className="emmy-awards__collection"
                data={section1}
                loadings={section1Loadings}
                loadingRows={2}
                analyticsLocation={LOCATIONS_EMMY_AWARDS_PAGE}
                handleUpdateAppStatus={this.handleUpdateAppStatus}
              />
            </div>
            {!isAuth && <CreateFreeProfile />}
            {hasSection2 && (
              <div className="page-content">
                <Swimlanes
                  className="emmy-awards__collection"
                  data={section2}
                  loadings={section2Loadings}
                  loadingRows={2}
                  analyticsLocation={LOCATIONS_EMMY_AWARDS_PAGE}
                  handleUpdateAppStatus={this.handleUpdateAppStatus}
                />
              </div>
            )}
          </div>
        </Container>
      </Layout>
    );
  }
}

EmmyAwards.propTypes = {
  t: PropTypes.func.isRequired,
  isIframe: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  section1: PropTypes.object,
  section1Loadings: PropTypes.object.isRequired,
  section2: PropTypes.object,
  section2Loadings: PropTypes.object.isRequired,
  getEmmyAwardsDetails: PropTypes.func.isRequired,
  swimlanesClear: PropTypes.func.isRequired,
  appListUpdateAppStatus: PropTypes.func.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ app, auth, swimlanes, partner }) => ({
  isIframe: app.isIframe,
  isAuth: auth.isAuth,
  section1: swimlanes.section1[EMMY_AWARDS_PAGE] || {},
  section2: swimlanes.section2[EMMY_AWARDS_PAGE] || {},
  section1Loadings: swimlanes.loadings.section1[EMMY_AWARDS_PAGE] || {},
  section2Loadings: swimlanes.loadings.section2[EMMY_AWARDS_PAGE] || {},
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
});

const mapDispatchToProps = dispatch => ({
  getEmmyAwardsDetails: () => {
    dispatch(EmmyAwardsActions.getEmmyAwardsDetails.action());
  },
  swimlanesClear: () => dispatch(SwimlanesActions.swimlanesClear.action()),
  appListUpdateAppStatus: (id, status) => {
    dispatch(AppDetailsActions.appListUpdateAppStatus.action({ id, status }));
  },
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(EmmyAwards);
